import React, { ReactElement, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { logout } from 'core/actions/Auth.actions';
import { IAppState } from "core/store/store";
import { connect } from "react-redux";
import { translate } from "shared/helpers/utils";
import { withLocalize, LocalizeContextProps } from 'react-localize-redux'
import { RouterConfig } from "core/utils/constants";
import { FeatureFlags } from 'core/constants';
import { openMiniCart } from 'secure-pages/ecommerce/EcommerceRouteComponents'
import { useLocalStorageUrl } from 'shared/helpers/useLocalStorageUrl'

interface TopNavProps extends LocalizeContextProps {
  userId: string;
  userApplications: Array<{ is_retail?: boolean }>;
  userProfileFetched: boolean;
  isUnifiedRoleActive: boolean;
  featuresLoaded: boolean;
  logout: () => void;
}

const TopNav = ({ userId, userApplications, userProfileFetched, isUnifiedRoleActive, featuresLoaded, logout }: TopNavProps): ReactElement => {
  const navigate = useHistory();
  // Track the settings key used for the last MFE init so we can detect when menu-relevant
  // flags (isUnifiedRoleActive, hasRetailApp) change after the initial load and re-initialize.
  const lastInitSettingsKey = useRef<string | null>(null);
  const manifestDomain = useLocalStorageUrl('persistent_header_url', window.location.origin);
  const USER_DETAILS_URL = '/app/account/access/administrators/' + userId;

  const hasRetailApp = Array.isArray(userApplications) && userApplications.some((app) => app.is_retail === true);

  const settingsMenu = [
    ...(isUnifiedRoleActive ? [{
      label: translate('header.settings.unifiedUserManagement') as string,
      externalLink: false,
      onClick: (e: React.MouseEvent) => { e.preventDefault(); navigate.push(RouterConfig.unifiedUserManagement.administrators()); },
    }] : []),
    ...(hasRetailApp ? [{
      label: translate('header.settings.billingAndSubscription') as string,
      externalLink: false,
      onClick: (e: React.MouseEvent) => { e.preventDefault(); navigate.push(RouterConfig.urlMap.billingAndSubscription); },
    }] : []),
  ];

  type ProductSwitcherData = {
    id?: string;
    app_name?: string;
    name?: string;
    icon?: string;
    url?: string;
    new_tab?: boolean;
  };
  const handleProductSwitcherCallback = (
    e: React.MouseEvent<HTMLElement>,
    data: ProductSwitcherData
  ): void => {
    e.preventDefault();
    if (data?.url) {
      try {
        const parsed = new URL(data.url, window.location.origin);
        const isHttpProtocol = parsed.protocol === "https:"

        if (!isHttpProtocol) {
          console.warn("Blocked navigation to disallowed URL:", parsed.href);
          return;
        }

        const isSameOrigin = parsed.origin === window.location.origin;

        if (data.new_tab) {
          window.open(parsed.toString(), "_blank", "noopener,noreferrer");
        } else if (isSameOrigin) {
          navigate.push(parsed.pathname + parsed.search + parsed.hash);
        } else {
          window.location.href = parsed.toString();
        }
      } catch {
        console.warn("Blocked navigation to invalid URL");
      }
    }
  };

  const loadMd = () => {
    try {
      if (window.dcSpaMicroFrontendLoader?.init) {
        window.dcSpaMicroFrontendLoader.init([
          {
            containerId: "header-hub-container",
            microAppName: "header-hub-mfe",
            manifestDomain: manifestDomain,
            noAppPrefix: true,
            disableHashNavigation: true,
            headerConfig: {
              logout: logout,
              navigate: (url: string) => navigate.push(url),
              productSwitcherHeadline: translate('header.productSwitcherHeadline') as string,
              productSwitcherLeftAlign: false,
              productSwitcherCallback: handleProductSwitcherCallback,
              profileUrl: USER_DETAILS_URL,
              openProfileUrlInNewTab: false,
              profileLabel: translate('header.profile') as string,
              logoutLabel: translate('header.logout') as string,
              customHelpMenuItems: undefined,
              additionalMenuItems: undefined,
              settingsMenuItems: isUnifiedRoleActive || hasRetailApp ? settingsMenu : undefined,
              cart: {
                onClick: () => openMiniCart()
              }
            },
          },
        ]);
      } else {
        // Optionally: log, retry, or handle gracefully
        console.error(
          "dcSpaMicroFrontendLoader or its init method is not available"
        );
      }
    } catch (error) {
      console.error("mf failed to load", error);
    }
  };

  useEffect(() => {
    if (!userId || !featuresLoaded || !userProfileFetched) return;
    const settingsKey = `${isUnifiedRoleActive}-${hasRetailApp}`;
    if (lastInitSettingsKey.current === settingsKey) return;

    // dcSpaMicroFrontendLoader is injected by a <script> tag — poll briefly if it's not ready yet
    const MAX_WAIT_MS = 5_000;
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (window.dcSpaMicroFrontendLoader?.init) {
        clearInterval(interval);
        loadMd();
        lastInitSettingsKey.current = settingsKey;
      } else if (Date.now() - startTime >= MAX_WAIT_MS) {
        clearInterval(interval);
        console.error('[TopNav] dcSpaMicroFrontendLoader not available after 5s — header-hub MFE will not load');
      }
    }, 100);

    return () => clearInterval(interval);
  }, [manifestDomain, userId, featuresLoaded, isUnifiedRoleActive, hasRetailApp, userProfileFetched]);

  // Intercept clicks inside the MFE container so internal-path navigations go
  // through React Router (History.push) instead of triggering a full page reload.
  useEffect(() => {
    const container = document.getElementById('header-hub-container');
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      // Only handle unmodified left-clicks
      if (e.defaultPrevented) {
        return;
      }
      if (e.button !== 0) {
        // Not a primary button click (e.g., middle or right click)
        return;
      }
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        // Let browser handle new-tab/window or other modified clicks
        return;
      }

      const target = e.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const anchor = target.closest('a');
      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href) {
        return;
      }

      const targetAttr = anchor.getAttribute('target');
      if (targetAttr && targetAttr.toLowerCase() === '_blank') {
        // Respect explicit new-tab behavior
        return;
      }

      if (anchor.hasAttribute('download')) {
        // Let the browser handle downloads
        return;
      }

      // Only intercept safe internal relative paths (e.g., "/path"),
      // and avoid protocol-relative URLs ("//example.com")
      if (!href.startsWith('/') || href.startsWith('//')) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      navigate.push(href);
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, []);

  return (
    <>
      <div id="header-hub-container"></div>
    </>
  );
};


export default connect(
  (state: IAppState) => ({
    userId: state.profile.data ? state.profile.data.id : '',
    userGroups: state.profile.data ? state.profile.data.groups : [],
    userApplications: state.userProfile.data ? state.userProfile.data.applications : [],
    userProfileFetched: state.userProfile.data !== undefined,
    isUnifiedRoleActive: !!(state.features as any)?.[FeatureFlags.ENABLE_UNIFIED_USER_MANAGEMENT],
    featuresLoaded: state.features.isLoading === false,
  }),
  { logout }
)(withLocalize(TopNav))
